import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import {
  Notification,
  NotificationFilter,
} from 'app/layout/common/notifications/notifications.types';
import { REMINDER_NOTIFICATION_ICON } from 'app/core/utils/notification-icon.util';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';

/**
 * Header clock/alarm inbox — order-cycle overdue + reminder notifications.
 * Opens a dropdown panel (same pattern as the bell), not the overdue report page.
 */
@Component({
  selector: 'reminders-header-button',
  standalone: true,
  templateUrl: './reminders-header-button.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'remindersHeaderButton',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    NgClass,
    DatePipe,
  ],
})
export class RemindersHeaderButtonComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationsService = inject(NotificationsService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('remindersOrigin') private remindersOrigin: MatButton;
  @ViewChild('remindersPanel') private remindersPanel: TemplateRef<unknown>;

  readonly alarmIcon = REMINDER_NOTIFICATION_ICON;

  notifications: Notification[] = [];
  filteredNotifications: Notification[] = [];
  unreadCount = 0;
  totalCount = 0;
  activeFilter: NotificationFilter = 'all';
  loading = false;

  private overlayRef: OverlayRef | null = null;

  ngOnInit(): void {
    this.notificationsService.clockNotifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notifications) => {
        this.notifications = notifications ?? [];
        this._applyFilter();
        this.cdr.markForCheck();
      });

    this.notificationsService.clockUnreadCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.unreadCount = count;
        this.cdr.markForCheck();
      });

    this.notificationsService.clockTotalCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.totalCount = count;
        this.cdr.markForCheck();
      });

    interval(60_000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.notificationsService.getAll().pipe(catchError(() => of([])))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  get allCount(): number {
    return this.totalCount || this.notifications.length;
  }

  get displayUnreadCount(): number {
    const localUnread = this.notifications.filter((n) => !n.read).length;
    return Math.max(this.unreadCount, localUnread);
  }

  get displayReadCount(): number {
    return this.notifications.filter((n) => n.read).length;
  }

  openPanel(): void {
    if (!this.remindersPanel || !this.remindersOrigin) {
      return;
    }

    if (!this.overlayRef) {
      this._createOverlay();
    }

    this.overlayRef.attach(
      new TemplatePortal(this.remindersPanel, this.viewContainerRef)
    );

    this.cdr.detectChanges();
    this._loadNotifications();
  }

  closePanel(): void {
    this.overlayRef?.detach();
  }

  setFilter(filter: NotificationFilter): void {
    this.activeFilter = filter;
    this._applyFilter();
    this.cdr.markForCheck();
  }

  toggleExpand(notification: Notification, event?: Event): void {
    event?.stopPropagation();
    notification.expanded = !notification.expanded;
    this.cdr.detectChanges();
  }

  isExpanded(notification: Notification): boolean {
    return !!notification.expanded;
  }

  openNotification(notification: Notification, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    const finish = (): void => {
      notification.expanded = false;
      this.closePanel();

      if (notification.link) {
        void this.router.navigateByUrl(notification.link);
      }

      this.cdr.markForCheck();
    };

    if (!notification.read) {
      this.notificationsService
        .markAsRead(notification.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            notification.read = true;
            this._applyFilter();
            finish();
          },
          error: () => finish(),
        });
      return;
    }

    finish();
  }

  dismiss(notification: Notification, event: Event): void {
    event.stopPropagation();
    this.notificationsService
      .archive(notification.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  markAllAsRead(): void {
    if (this.displayUnreadCount === 0) {
      return;
    }

    this.notificationsService
      .markAllAsRead('clock')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cdr.markForCheck(),
      });
  }

  clearAll(): void {
    if (!this.notifications.length) {
      return;
    }

    this.notificationsService
      .deleteAll('clock')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private _loadNotifications(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.notificationsService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false;
          this._applyFilter();
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private _applyFilter(): void {
    switch (this.activeFilter) {
      case 'unread':
        this.filteredNotifications = this.notifications.filter((n) => !n.read);
        break;
      case 'read':
        this.filteredNotifications = this.notifications.filter((n) => n.read);
        break;
      default:
        this.filteredNotifications = [...this.notifications];
        break;
    }
  }

  private _createOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.remindersOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.overlayRef?.detach();
      });
  }
}
